/**
 * VendorPortalProductService
 * Service for vendor product management (CRUD operations on own products).
 * Route: api/vendor-portal/products
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalProductService
 * Handles vendor product operations for the vendor portal.
 * Maps to /api/vendor-portal/products endpoint
 */
export class VendorPortalProductService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalProductService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalProductService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all products for the current vendor (enriched view model)
     * GET /api/vendor-portal/products
     * Returns VendorProductModelView with vendor, type, status details
     */
    async getProducts() {
        const response = await this.getProductsRaw();
        return response.data ?? [];
    }
    /**
     * Get all products (raw response with enriched view model)
     */
    getProductsRaw() {
        return this.get();
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific product by ID
     * GET /api/vendor-portal/products/{id}
     */
    async getProduct(id) {
        const response = await this.getProductRaw(id);
        if (!response.data) {
            throw new Error(`Product with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific product (raw response)
     */
    getProductRaw(id) {
        return this.get(String(id));
    }
    // ==================== CREATE ====================
    /**
     * Create a new product for the current vendor
     * POST /api/vendor-portal/products
     */
    async createProduct(data) {
        const response = await this.createProductRaw(data);
        if (!response.data) {
            throw new Error('Failed to create product');
        }
        return response.data;
    }
    /**
     * Create a new product (raw response)
     */
    createProductRaw(data) {
        return this.post('', data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing product
     * PUT /api/vendor-portal/products/{id}
     */
    async updateProduct(id, data) {
        const response = await this.updateProductRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update product with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing product (raw response)
     */
    updateProductRaw(id, data) {
        return this.put(String(id), data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a product
     * DELETE /api/vendor-portal/products/{id}
     */
    async deleteProduct(id) {
        const response = await this.deleteProductRaw(id);
        return response.success;
    }
    /**
     * Delete a product (raw response)
     */
    deleteProductRaw(id) {
        return this.delete(String(id));
    }
    // ==================== BULK IMPORT ====================
    /**
     * Bulk import products for the current vendor
     * POST /api/vendor-portal/products/bulk-import
     */
    async bulkImportProducts(data) {
        const response = await this.bulkImportProductsRaw(data);
        if (!response.data) {
            throw new Error('Failed to import products');
        }
        return response.data;
    }
    /**
     * Bulk import products (raw response)
     */
    bulkImportProductsRaw(data) {
        return this.post('bulk-import', data);
    }
}
VendorPortalProductService.DEFAULT_SERVICE_PATH = 'vendor-catalogs';
VendorPortalProductService.BASE_PATH = 'api/vendor-portal/products';
//# sourceMappingURL=VendorPortalProductService.js.map